import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { SessionProvider } from "./context/SessionContext";
import { UserProvider, useUser } from "./context/UserContext";
import { AppProvider } from "./context/AppContext"; // Import the App Provider
import { DeviceProvider } from "./context/DeviceContext"; // Import the App Provider
import { AdminDataProvider } from "./context/AdminDataContext"; // Import the Admin Data Provider
import LoadingScreen from "./components/LoadingScreen"; // Import the new component
import MaintenanceScreen from "./components/MaintenanceScreen"; // Import maintenance screen
import AdminDataStatus from "./components/AdminDataStatus"; // Import data status component
import AdminLayout from "./components/AdminLayout"; // Import admin layout wrapper
import useForceRefresh from "./hooks/useForceRefresh"; // Import the hook
import useMaintenanceStatus from "./hooks/useMaintenanceStatus"; // Import maintenance hook


// Lazy loading pages
const AccountLogInPage = React.lazy(() => import("./pages/AccountLogInPage"));
const MainForm = React.lazy(() => import("./pages/MainForm"));
const HomePage = React.lazy(() => import("./pages/HomePage"));
const NewEnrolmentForm = React.lazy(() => import("./pages/NewEnrolmentForm"));
const AdminCentre = React.lazy(() => import("./pages/admin/AdminCentre"));
const ManageSessionsPage = React.lazy(() => import("./pages/admin/ManageSessionsPage"));
const EditSessionPage = React.lazy(() => import("./pages/admin/EditSessionPage"));
const ParentAccessPage = React.lazy(() => import("./pages/admin/ParentAccessPage"));
const EmailParentsPage = React.lazy(() => import("./pages/admin/EmailParentsPage"));
const AdminSettingsPage = React.lazy(() => import("./pages/admin/AdminSettingsPage"));
const EmailLogPage = React.lazy(() => import("./pages/admin/EmailLogPage"));
const EmailLogDetailPage = React.lazy(() => import("./pages/admin/EmailLogDetailPage"));
const AdminDashboardPage = React.lazy(() => import("./pages/admin/AdminDashboardPage"));
const ManageSacramentsPage = React.lazy(() => import("./pages/admin/ManageSacramentsPage"));
const EditSacramentPage = React.lazy(() => import("./pages/admin/EditSacramentPage"));
const ConfirmEnrolmentPage = React.lazy(() => import("./pages/admin/ConfirmEnrolmentPage"));
const EnrolmentTablePage = React.lazy(() => import("./pages/admin/EnrolmentTablePage"));
const AttendanceListPage = React.lazy(() => import("./pages/admin/AttendanceListPage"));
const EnrolmentSubmittedPage = React.lazy(() => import("./pages/EnrolmentSubmittedPage")); // New import
const MaintenanceManager = React.lazy(() => import("./components/admin/MaintenanceManager")); // New import




const roleHierarchy = {
  parent: ["parent"],
  assistant: ["assistant", "parent"],
  parishAdmin: ["parishAdmin", "assistant", "parent"],
  globalAdmin: ["globalAdmin", "parishAdmin", "assistant", "parent"],
  developer: ["developer", "globalAdmin", "parishAdmin", "assistant", "parent"],
};


// Protected Route Component
const ProtectedRoute = ({ allowedRoles = [], children }) => {
  const { userData, activeContext, loading } = useUser();

  if (loading) {
    console.log("ProtectedRoute: Loading...");
    return <p>Loading...</p>;
  }

  // Always prioritize userData.role unless it is "parent" or "guest"
  const roleToCheck =
    userData?.role && userData.role !== "parent" && userData.role !== "guest"
      ? userData.role
      : activeContext?.role || userData?.role || "guest";

  console.log("ProtectedRoute: userData.role", userData?.role);
  console.log("ProtectedRoute: activeContext.role", activeContext?.role);
  console.log("ProtectedRoute: roleToCheck", roleToCheck);
  console.log("ProtectedRoute: allowedRoles", allowedRoles);

  if (!roleHierarchy[roleToCheck]) {
    console.log("ProtectedRoute: Access denied. Invalid role:", roleToCheck);
    return <Navigate to="/login" replace />;
  }

  const effectiveRoles = roleHierarchy[roleToCheck];
  console.log("ProtectedRoute: effectiveRoles", effectiveRoles);

  const hasAccess = allowedRoles.some((role) => effectiveRoles.includes(role));
  if (!hasAccess) {
    console.log(
      `ProtectedRoute: Access denied. Role '${roleToCheck}' not in allowedRoles: ${allowedRoles}`
    );
    return <Navigate to="/" replace />;
  }

  console.log("ProtectedRoute: Access granted.");
  return children;
};


function App() {
  const { user, userData, loading } = useUser();

  useForceRefresh(); // 🚀 Calls the hook

  // Determine user role for maintenance check
  // CRITICAL FIX: Don't default to 'parent' until we have actual user data
  // This prevents admin users from being checked against parent maintenance on initial login
  const userRole = userData?.role;
  const isUserDataLoaded = user && userData; // Only proceed when we have both user and userData
  
  console.log('🎯 App - userRole determined:', userRole);
  console.log('🎯 App - userData:', JSON.stringify(userData, null, 2));
  console.log('🎯 App - isUserDataLoaded:', isUserDataLoaded);
  console.log('🎯 App - user exists:', !!user);
  console.log('🎯 App - userData exists:', !!userData);
  console.log('🎯 App - userData.role:', userData?.role);
  
  // FIXED LOGIC: More explicit handling
  let maintenanceCheckRole;
  if (!user) {
    // No user logged in - check parent maintenance (for login page)
    maintenanceCheckRole = 'parent';
    console.log('🎯 App - No user logged in, checking parent maintenance');
  } else if (!userData || !userData.role) {
    // User logged in but no role data yet - wait (stay in loading)
    maintenanceCheckRole = null;
    console.log('🎯 App - User logged in but no role data, staying in loading state');
  } else {
    // User logged in with role data - use actual role
    maintenanceCheckRole = userData.role;
    console.log('🎯 App - User role confirmed:', maintenanceCheckRole);
  }
  
  // Only check maintenance status if we have loaded user data or no user is logged in
  const { isLoading: maintenanceLoading, isMaintenanceMode, config, isAdmin } = useMaintenanceStatus(maintenanceCheckRole);

  console.log("🎯 App - user:", user);
  console.log("🎯 App - userData:", userData);
  console.log("🎯 App - maintenanceCheckRole:", maintenanceCheckRole);
  console.log("🎯 App - maintenance status:", { 
    isLoading: maintenanceLoading, 
    isMaintenanceMode, 
    config: JSON.stringify(config, null, 2), 
    isAdmin 
  });

  // Show loading screen while checking user authentication, user data, or maintenance status
  // CRITICAL: Wait for user data to load completely before making maintenance decisions
  if (loading || (user && !userData) || (maintenanceCheckRole === null) || maintenanceLoading) {
    console.log('🎯 App - Showing loading screen:', {
      loading,
      userWithoutData: (user && !userData),
      waitingForRole: (maintenanceCheckRole === null),
      maintenanceLoading
    });
    return <LoadingScreen />;
  }

  // Show maintenance screen if maintenance mode is enabled
  if (isMaintenanceMode && config) {
    console.log('🎯 App - Showing maintenance screen with config:', JSON.stringify(config, null, 2));
    return (
      <MaintenanceScreen
        title={config.title}
        message={config.message}
        isAdmin={isAdmin}
      />
    );
  }

  console.log('🎯 App - Showing normal app (no maintenance)');

  return (
    <SessionProvider>
      <Router>
        <Suspense fallback={<LoadingScreen message="Loading Page..." />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<AccountLogInPage />} />

            {/* Parent Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute allowedRoles={["parent", "parishAdmin", "globalAdmin"]}>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new-enrolment"
              element={
                <ProtectedRoute allowedRoles={["parent", "parishAdmin", "globalAdmin"]}>
                  <NewEnrolmentForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enrolment-form"
              element={
                <ProtectedRoute allowedRoles={["parent", "parishAdmin", "globalAdmin"]}>
                  <MainForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/next-steps"
              element={
                <ProtectedRoute allowedRoles={["parent", "parishAdmin", "globalAdmin"]}>
                  <EnrolmentSubmittedPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["parishAdmin", "assistant", "globalAdmin", "developer"]}>
                  <AdminLayout>
                    <AdminCentre />
                  </AdminLayout>
                </ProtectedRoute>
              }
            >
            </Route>

            <Route
              path="/admin/email-parents"
              element={
                <ProtectedRoute allowedRoles={["parishAdmin", "globalAdmin", "developer"]}>
                  <AdminLayout>
                    <EmailParentsPage />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/edit-sacrament"
              element={
                <ProtectedRoute allowedRoles={["parishAdmin", "globalAdmin", "developer"]}>
                  <EditSacramentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/session"
              element={
                <ProtectedRoute allowedRoles={["parishAdmin", "globalAdmin", "developer"]}>
                  <EditSessionPage />
                </ProtectedRoute>
              }
            >
            </Route>

            <Route
              path="/admin/maintenance"
              element={
                <ProtectedRoute allowedRoles={["globalAdmin", "developer"]}>
                  <MaintenanceManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/email-log"
              element={
                <ProtectedRoute allowedRoles={["parishAdmin", "globalAdmin", "developer"]}>
                  <EmailLogPage />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/admin/email-log/:logId"
              element={
                <ProtectedRoute allowedRoles={["parishAdmin", "globalAdmin", "developer"]}>
                  <EmailLogDetailPage />
                </ProtectedRoute>
              }
            />



            {/* Default Fallback */}
            <Route
              path="*"
              element={
                user ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
              }
            />
          </Routes>
        </Suspense>
      </Router>
    </SessionProvider>
  );
}

export default function AppWithProviders() {
  return (
    <UserProvider>
      <AppProvider> {/* Wrap App with AppProvider */}
        <AdminDataProvider> {/* Wrap with Admin Data Context for real-time caching */}
          <DeviceProvider> {/* Wrap with Device Context */}
            <App />
          </DeviceProvider>
        </AdminDataProvider>
      </AppProvider>
    </UserProvider>
  );
}